import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ArtistDetail {
  id: string;
  artistid: string;
  email_address: string;
  full_name: string;
  phone_number: string | null;
  whatsapp_number: string | null;
  full_address: string | null;
  city_state: string | null;
  pin_code: string | null;
  bank_account_details: string | null;
  upi_id: string | null;
  availability: string | null;
  status: 'pending' | 'reviewed' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export interface CreateArtistDetailData {
  email_address: string;
  full_name: string;
  phone_number?: string;
  whatsapp_number?: string;
  full_address?: string;
  city_state?: string;
  pin_code?: string;
  bank_account_details?: string;
  upi_id?: string;
  availability?: string;
  status?: 'pending' | 'reviewed' | 'approved' | 'rejected';
}

export interface UpdateArtistDetailData {
  email_address?: string;
  full_name?: string;
  phone_number?: string;
  whatsapp_number?: string;
  full_address?: string;
  city_state?: string;
  pin_code?: string;
  bank_account_details?: string;
  upi_id?: string;
  availability?: string;
  status?: 'pending' | 'reviewed' | 'approved' | 'rejected';
}

export function useArtistDetails() {
  const [artistDetails, setArtistDetails] = useState<ArtistDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all artist details
  const fetchArtistDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('artist_details')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setArtistDetails(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Create new artist detail
  const createArtistDetail = async (data: CreateArtistDetailData): Promise<ArtistDetail | null> => {
    try {
      setError(null);
      
      const { data: newArtistDetail, error } = await supabase
        .from('artist_details')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      
      setArtistDetails(prev => [newArtistDetail, ...prev]);
      return newArtistDetail;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    }
  };

  // Update artist detail
  const updateArtistDetail = async (id: string, data: UpdateArtistDetailData): Promise<ArtistDetail | null> => {
    try {
      setError(null);
      
      const { data: updatedArtistDetail, error } = await supabase
        .from('artist_details')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setArtistDetails(prev => 
        prev.map(item => item.id === id ? updatedArtistDetail : item)
      );
      
      return updatedArtistDetail;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    }
  };

  // Delete artist detail
  const deleteArtistDetail = async (id: string): Promise<boolean> => {
    try {
      setError(null);
      
      const { error } = await supabase
        .from('artist_details')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setArtistDetails(prev => prev.filter(item => item.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return false;
    }
  };

  // Update status
  const updateStatus = async (id: string, status: ArtistDetail['status']): Promise<boolean> => {
    try {
      setError(null);
      
      const { error } = await supabase
        .from('artist_details')
        .update({ status })
        .eq('id', id);

      if (error) throw error;
      
      setArtistDetails(prev => 
        prev.map(item => item.id === id ? { ...item, status } : item)
      );
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return false;
    }
  };

  // Get artist detail by ID
  const getArtistDetailById = async (id: string): Promise<ArtistDetail | null> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from('artist_details')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    }
  };

  // Search artist details
  const searchArtistDetails = async (query: string): Promise<ArtistDetail[]> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from('artist_details')
        .select('*')
        .or(`artistid.ilike.%${query}%,full_name.ilike.%${query}%,email_address.ilike.%${query}%,city_state.ilike.%${query}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return data || [];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return [];
    }
  };

  // Filter by status
  const filterByStatus = async (status: string): Promise<ArtistDetail[]> => {
    try {
      setError(null);
      
      if (status === 'all') {
        return await fetchArtistDetails();
      }
      
      const { data, error } = await supabase
        .from('artist_details')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return data || [];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return [];
    }
  };

  // Get statistics
  const getStatistics = () => {
    const total = artistDetails.length;
    const pending = artistDetails.filter(item => item.status === 'pending').length;
    const reviewed = artistDetails.filter(item => item.status === 'reviewed').length;
    const approved = artistDetails.filter(item => item.status === 'approved').length;
    const rejected = artistDetails.filter(item => item.status === 'rejected').length;

    return { total, pending, reviewed, approved, rejected };
  };

  useEffect(() => {
    fetchArtistDetails();
  }, []);

  return {
    artistDetails,
    loading,
    error,
    fetchArtistDetails,
    createArtistDetail,
    updateArtistDetail,
    deleteArtistDetail,
    updateStatus,
    getArtistDetailById,
    searchArtistDetails,
    filterByStatus,
    getStatistics,
  };
}
